/**
 * Spoken summaries of a live round, for the `aria-live` region (roadmap W3 / P10).
 *
 * Why a summary and not `aria-live` on the results themselves: a wordcloud or a leaderboard is a
 * dozen elements that all change at once, several times a minute. Marking that region live makes a
 * screen reader read the whole thing on every tick, which is worse than silence — the user cannot
 * follow the room OR the question. A short sentence that states what changed is the difference
 * between "zero signal" and "usable", and it costs one element.
 *
 * Pure functions over the aggregate, so they are testable without a DOM and cannot drift from what
 * the screens render.
 */
import type { DeliveryClock } from "@klapzo/kit-core";
import { getLocale, t } from "./i18n.js";
import { answeredIn, barTotal, formatMean, isWithheldList, meanOf, type McOption, type QuestionAggregate, type LeaderboardEntry, type TeamStanding } from "./api.js";

/** Options are needed to name an mc leader — the aggregate carries ids, not labels. */
export interface AnnounceOptions {
  /**
   * The round's own option table, from the `question` frame. `McOption`, not `{id,label}`: a scale
   * round's `value`s live here and nowhere else on a LIVE round, so narrowing this type is what
   * silently dropped the spoken mean (round-1 review).
   */
  options?: ReadonlyArray<McOption>;
  /**
   * The round's own config and whether it has closed — the two things `isWithheldList` needs.
   *
   * Passed in rather than derived, because a withheld word cloud and an unanswered one produce the
   * SAME arm: an empty list. Without the config the summary said "0 perguntas" to a screen reader
   * for a round twelve people had answered, and read the running word count aloud on a round whose
   * whole point was that nobody sees it yet (round-1 review, item D).
   */
  question?: { revealTally?: boolean } | null;
  closed?: boolean;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${t(n === 1 ? one : many)}`;
}

/**
 * One sentence describing the current state of the round, or "" when there is nothing to say.
 *
 * Empty string rather than a "waiting" phrase on purpose: an empty live region announces nothing,
 * and a screen reader repeating "no results yet" every time a socket frame lands is noise.
 */
export function describeAggregate(agg: QuestionAggregate | null, opts: AnnounceOptions = {}): string {
  if (!agg) return "";
  return sentence(summarise(agg, opts));
}

/**
 * Terminate the sentence. Not cosmetic: screen readers use the full stop for prosody and for the
 * pause between an announcement and whatever the user is reading — an unterminated fragment runs
 * into the next utterance.
 */
function sentence(text: string): string {
  if (!text) return "";
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/**
 * What a withheld word cloud / Q&A round has to say: how many people answered, and that the result
 * is not out yet — the same two facts, in the same words, as the mc branch and as `WithheldNote`.
 * No new i18n key: `mc.answered`/`mc.tallyHidden` already say this in all three locales.
 *
 * The review count rides along (round-2 review, item O). It is content-free — the number of
 * submissions waiting, never a word of any of them — and it is the one thing on this arm the host
 * needs SPOKEN: withholding the result is not a reason to stop telling them there is a queue to
 * open. The `open` branch below already said it, and short-circuiting to this sentence had silently
 * dropped it.
 */
function withheldSentence(agg: QuestionAggregate): string {
  const answered = plural(answeredIn(agg) ?? 0, "mc.answeredOne", "mc.answered");
  const queue = agg.type === "open" && agg.pendingCount > 0 ? ` ${agg.pendingCount} ${t("a11y.inReview")}.` : "";
  return `${answered}. ${t("mc.tallyHidden")}.${queue}`;
}

function summarise(agg: QuestionAggregate, opts: AnnounceOptions): string {
  if (isWithheldList(opts.question, opts.closed === true, agg)) return withheldSentence(agg);
  switch (agg.type) {
    case "mc": {
      const answered = plural(agg.total, "mc.answeredOne", "mc.answered");
      if (agg.tally === "hidden") return `${answered}. ${t("mc.tallyHidden")}.`;
      if (agg.total === 0) return answered;
      // The leading option, named — a bar chart's shape is the one thing a summary can carry.
      const leading = Object.entries(agg.counts).reduce<[string, number]>(
        (best, [id, n]) => (n > best[1] ? [id, n] : best),
        ["", -1],
      );
      const label = opts.options?.find((o) => o.id === leading[0])?.label;
      if (!label) return answered;
      // The denominator is points on a priority round, respondents on a vote — otherwise a room of
      // three spending 100 points each hears "Onboarding: 120 (4000%)".
      const pct = Math.round((leading[1] / barTotal(agg)) * 100);
      // ...and the number is named, because "120" after "3 responderam" is otherwise unreadable.
      const unit = agg.payload !== undefined ? ` ${t("prio.unit")}` : "";
      // On a scale round the AVERAGE is the headline, so it is spoken too (W9b, US-LV-5 AC2) —
      // appended rather than substituted: the leading point is still the shape of the bar chart,
      // and a screen-reader user should hear the same two facts the wall shows.
      // The persisted record's table when there is one, the `question` frame's otherwise — a LIVE
      // aggregate carries no options at all (round-1 review; see `MeanNote`).
      const mean = agg.aggregate === "mean" ? meanOf(agg.counts, agg.options ?? opts.options, agg.total) : null;
      const average = mean === null ? "" : ` ${t("scale.mean", { v: formatMean(mean, getLocale()) })}.`;
      return `${answered}. ${label}: ${leading[1]}${unit} (${pct}%).${average}`;
    }
    case "wordcloud": {
      const words = plural(agg.total, "a11y.wordOne", "a11y.words");
      const top = agg.words[0];
      return top ? `${words}. ${t("a11y.mostCited")}: ${top.word} (${top.count}).` : words;
    }
    case "open": {
      const asked = plural(agg.entries.length, "a11y.questionOne", "a11y.questions");
      // The review count is content-free and the host needs it spoken; the text never is.
      return agg.pendingCount > 0 ? `${asked}. ${agg.pendingCount} ${t("a11y.inReview")}.` : asked;
    }
    case "promptvote":
      if (agg.phase === "write") return plural(agg.submissions, "pv.submissionOne", "pv.submissions");
      if (agg.phase === "vote") return `${t("pv.vote")}. ${agg.votesCast} ${t("pv.votesCast")}.`;
      // Reveal reads the winner only. Reading every entry is what the visible list is for, and an
      // anonymous round has no author to name — the type makes that unreachable, not a flag check.
      if (!agg.results[0]) return t("pv.reveal");
      return [
        `${t("pv.reveal")}: ${agg.results[0].text}`,
        agg.authors === "named" ? ` — ${agg.results[0].nickname}` : "",
        `, ${plural(agg.results[0].votes, "pv.voteOne", "pv.votes")}.`,
      ].join("");
    case "picker":
      // Spinning says how many are in the hat and NOT the result — the type has no result to reach
      // for, which is the same guarantee the visible screens get.
      if (agg.phase === "spinning") {
        return `${t("picker.title")}: ${plural(agg.candidates.length, "picker.candidateOne", "picker.candidates")}`;
      }
      // Drawn reads the OUTCOME, not the roster: a screen reader announcing seven teams member by
      // member talks over the room for half a minute. Who is on which team is on the projection and,
      // for the one person it matters to, on their own phone.
      if (agg.pick === "teams") {
        return `${t("picker.drawn")}: ${plural(agg.teams.length, "picker.teamOne", "picker.teamsWord")}`;
      }
      if (agg.pick === "rotation") {
        return agg.order[0] ? `${t("picker.drawn")}: ${t("picker.startsWith")} ${agg.order[0]}` : t("picker.nobody");
      }
      return agg.drawn ? `${t("picker.drawn")}: ${agg.drawn}` : t("picker.nobody");
  }
}

/**
 * Who is in front, once — the person, then the team. A ranked list read aloud in full is unusable on
 * a projector, and a team board read team by team is the same mistake twice.
 *
 * The team sentence stands on its own when nobody has an individual score yet: the standings appear
 * the moment the host splits the room, which is exactly when the individual board is still empty.
 */
export function describeLeaderboard(entries: readonly LeaderboardEntry[], teams: readonly TeamStanding[] = []): string {
  const top = entries[0];
  const team = teams[0];
  const person = top ? `${t("a11y.leader")}: ${top.nickname}, ${top.score}.` : "";
  if (!team) return person;
  return [person, `${t("teams.leader")}: ${team.name}, ${team.score}.`].filter(Boolean).join(" ");
}

/**
 * One sentence for the delivery clock (THE-1444), for the same `aria-live` region pattern as the
 * round summaries. Says WHAT changed in words — "over by 5 min" is a sentence, a red bar is not, so
 * an overrun never rides on colour alone. The caller announces on a change of block or state, not
 * on every tick: a clock that talks every 15 s is the noise `describeAggregate` was written to avoid.
 */
export function describeDelivery(clock: DeliveryClock): string {
  if (clock.phase === "notStarted") return "";
  if (clock.phase === "done") return sentence(t("planner.clock.done"));
  const onAir = t("planner.clock.onAir", { block: clock.block?.title ?? "" });
  const status =
    clock.blockOverrunMin > 0
      ? t("planner.clock.blockOver", { n: String(clock.blockOverrunMin) })
      : t("planner.clock.remaining", { n: String(clock.blockRemainingMin) });
  const agenda =
    clock.state === "over"
      ? t("planner.clock.agendaOver", { n: String(clock.agendaOverrunMin) })
      : clock.state === "ahead"
        ? t("planner.clock.agendaAhead", { n: String(clock.agendaAheadMin) })
        : t("planner.clock.onTime");
  return sentence(`${onAir}, ${status}, ${agenda}`);
}
